<template>
  <aw-spinner />
</template>

<script setup lang="ts">
  import { defineAsyncComponent, markRaw, h, onMounted } from 'vue';
  import { useNuxtApp, useHead, useId, navigateTo, useRoute } from 'nuxt/app';
  import { useContentMenusStore } from '~~/common/stores/contentMenus';
  import { useNotificationStore } from '~~/common/stores/notification';
  import { useModalStore } from '~~/common/stores/modal.js';
  import { isApiError } from '~~/common/utils/apiError';
  import AwSpinner from '~~/common/components/Common/AwSpinner.vue';
  const AwPasswordChangeForm = defineAsyncComponent(() => import('~~/shop/app/components/AwPasswordChangeForm.vue'));

  defineOptions({
    name: 'CareerPasswordChangePage',
  });

  const { $logger, $awt } = useNuxtApp();

  useHead({
    meta: [
      {
        name: 'robots',
        content: 'noindex,nofollow',
      },
    ],
  });

  const modalId = useId();
  const formId = useId();

  onMounted(async () => {
    try {
      const userHash = String(useRoute().params.hash);
      const redirectUrl = await getCareerRedirect();
      await navigateTo(`${redirectUrl}`);
      onShowCareerPwdChangeModal({ userHash });
    } catch (err) {
      $logger.error(err);
      try {
        const notificationStore = useNotificationStore();
        notificationStore.pushError({
          text: {
            title: errorTitle(err),
          },
        });
      } catch (err1) {
        if (err1 instanceof Error) {
          err1.cause = err;
        }
        $logger.error(err1);
      }
    }
  });

  function errorTitle (err: unknown) {
    // `isApiError` only checks for a `response` key, so it is true for every axios error — the
    // backend message can still be missing, and then there is nothing to show but the error itself.
    const data = isApiError(err) ? err.response?.data : undefined;
    return data?.message || data?.title || data?.detail || (err instanceof Error ? err.message : String(err));
  }

  async function getCareerRedirect () {
    try {
      const { fetchContentMenu, getFirstLinkFromContentMenu } = useContentMenusStore();
      return getFirstLinkFromContentMenu(await fetchContentMenu('career-menu')) || '/';
    } catch (err) {
      $logger.error(err);
    }
  }

  function onShowCareerPwdChangeModal ({ userHash }: { userHash: string }) {
    const {
      addQueueSafeConfirmModal,
      removeQueueSafeConfirmModal,
    } = useModalStore();
    addQueueSafeConfirmModal({
      uuid: modalId,
      data: {
        closable: false,
        data: {
          confirmTitle: $awt('aw.password_change.title'),
          confirmText: '',
          confirmComponent: markRaw({
            render: () => h(
              AwPasswordChangeForm,
              {
                formId,
                userHash,
                isModal: true,
                onSuccess () {
                  removeQueueSafeConfirmModal({ uuid: modalId });
                },
              },
            ),
          }),
          buttons: {
            ok: {
              attrs: {
                type: 'submit',
                form: formId,
                onClick () {},
              },
            },
            cancel: {},
          },
        },
      },
    });
  }
</script>
